import { signProfileAsset } from "@/lib/profileAssetSigning.mjs";
// src/contexts/AuthContext.jsx
import React, {
  useCallback,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import supabase from "@/services/supabaseClient";
import { resolveValidatedSession } from "@/lib/authSession";
import { loadProgress } from "@/lib/persistStep";
import {
  clearLastRole as clearScopedLastRole,
  readLastRole as readScopedLastRole,
  writeLastRole as writeScopedLastRole,
} from "@/lib/roleCache";
import { ensureProfileFirstWrite } from "@/services/userTruthTracking.js";
import {
  applyTheme,
  persistThemeForUser,
  readStoredThemeForUser,
  VALID_THEMES,
} from "@/settings/theme.js";
import { resolveCanonicalRole } from "@/lib/roleResolution.mjs";
import { readAvatarSessionCache, writeAvatarSessionCache } from "@/lib/avatarSessionCache.js";
import { recordStagingPerformance } from "@/lib/stagingPerformanceTrace.js";

const PATIENT_PROGRESS_KEY = "onb_patient_v2";
const PRACTICE_TEAM_WELCOME_CACHE_KEY = "afrimed:practice-team:welcome-invite";
const PRACTICE_TEAM_WELCOME_CACHE_TTL_MS = 2 * 60 * 1000;

let practiceTeamMembershipRequest = null;
let profileHydrationRequest = null;

function readCachedPracticeTeamInvite(userId) {
  const uid = String(userId || "").trim();
  if (!uid || typeof window === "undefined") return null;

  try {
    const parsed = JSON.parse(window.sessionStorage?.getItem(PRACTICE_TEAM_WELCOME_CACHE_KEY) || "null");
    if (String(parsed?.user_id || "").trim() !== uid) return null;
    if (!parsed?.invite?.id) return null;
    const cachedAt = Number(parsed?.cached_at || 0);
    if (cachedAt && Date.now() - cachedAt > PRACTICE_TEAM_WELCOME_CACHE_TTL_MS) return null;
    return parsed.invite;
  } catch {
    return null;
  }
}

function writeCachedPracticeTeamInvite(userId, invite) {
  const uid = String(userId || "").trim();
  if (!uid || !invite?.id || typeof window === "undefined") return;

  try {
    window.sessionStorage?.setItem(
      PRACTICE_TEAM_WELCOME_CACHE_KEY,
      JSON.stringify({ user_id: uid, invite, cached_at: Date.now() })
    );
  } catch {
    // Best-effort cache only; auth truth remains the Edge Function.
  }
}

function clearCachedPracticeTeamInvite() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage?.removeItem(PRACTICE_TEAM_WELCOME_CACHE_KEY);
  } catch {
    // Best-effort cache invalidation. Auth truth is reloaded below.
  }
}

function normalizeKnownRole(value) {
  const role = String(value || "").toLowerCase().trim().replace(/[\s-]+/g, "_");
  if (role === "clinician" || role === "clinical" || role === "provider") return "doctor";
  if (role === "doctor_reviewer" || role === "clinical_reviewer") return "doctor";
  if (role === "patient_reviewer") return "patient";
  if (role === "pharmacist" || role === "pharmd" || role === "pharmacy_owner" || role === "pharmacy_staff") {
    return "pharmacy";
  }
  return role === "doctor" ||
    role === "patient" ||
    role === "admin" ||
    role === "support" ||
    role === "kyc_reviewer" ||
    role === "pharmacy" ||
    role === "practice_team" ||
    role === "super_admin" ||
    role === "moh_admin" ||
    role === "facility_admin" ||
    role === "institution_admin"
    ? role
    : role === "moh"
      ? "moh_admin"
    : "";
}

function isDevBypassIdentity(user) {
  return (
    user?.app_metadata?.bypass_dev_identity === true ||
    String(user?.id || "").trim() === "dev-user"
  );
}

function restoreUserThemePreference(user) {
  const userId = String(user?.id || "").trim();
  if (!userId) return;

  const localTheme = readStoredThemeForUser(userId);
  const metadataTheme = String(user?.user_metadata?.theme_preference || "").trim().toLowerCase();
  const nextTheme = localTheme || (VALID_THEMES.includes(metadataTheme) ? metadataTheme : "");
  if (!nextTheme) return;

  persistThemeForUser(userId, nextTheme);
  applyTheme(nextTheme);
}

// ✅ BYPASS is allowed ONLY on localhost during DEV.
// It will NEVER run on Netlify staging/prod even if env var is set.
function canBypassAuth() {
  try {
    const host = window.location.hostname;
    const isLocal =
      host === "localhost" ||
      host === "127.0.0.1" ||
      host === "0.0.0.0" ||
      host.endsWith(".local");
    return !!import.meta.env.DEV && isLocal && String(import.meta.env.VITE_BYPASS_AUTH || "") === "true";
  } catch {
    return false;
  }
}

function shouldSkipUserRoleLookup() {
  try {
    const pathname = String(window?.location?.pathname || "").trim();
    return pathname.startsWith("/settings/doctor/p/stripe-connect-launch");
  } catch {
    return false;
  }
}

// Helper: best-effort human name (prefer DB profile, then auth metadata, then email)
function resolveDisplayName(user, profile) {
  const fromProfile =
    (profile?.full_name && String(profile.full_name).trim()) ||
    ([profile?.first_name, profile?.last_name].filter(Boolean).join(" ").trim()) ||
    (profile?.display_name && String(profile.display_name).trim()) ||
    "";

  if (fromProfile) return fromProfile;

  const meta = user?.user_metadata || {};
  const fromMeta =
    meta.full_name ||
    meta.name ||
    meta.display_name ||
    meta.preferred_username ||
    "";

  const fromEmail = user?.email ? String(user.email).split("@")[0] : "";
  const best = String(fromMeta || fromEmail || "User").trim();
  return best || "User";
}

// Helper: best-effort avatar from auth metadata
function resolveAvatarFromUser(user) {
  const meta = user?.user_metadata || {};
  return (
    meta.avatar_url ||
    meta.picture ||
    meta.photo_url ||
    meta.profile_image_url ||
    ""
  );
}

function hasValue(value) {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.some(hasValue);
  if (typeof value === "object") return Object.values(value).some(hasValue);
  if (typeof value === "string") {
    const raw = value.trim();
    if (!raw) return false;
    if (raw === "[]" || raw === "{}" || raw === "null" || raw === "undefined") return false;
    if (raw.startsWith("[") || raw.startsWith("{")) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed.some(hasValue);
        if (parsed && typeof parsed === "object") return Object.values(parsed).some(hasValue);
      } catch {
        // ignore parse failures and keep raw string truthiness
      }
    }
    return true;
  }
  return true;
}

function hasMeaningfulDoctorProfile(row) {
  if (!row?.user_id) return false;
  return [
    row.onboarding_completed,
    row.full_name,
    row.first_name,
    row.last_name,
    row.specialty,
    row.speciality,
    row.about,
    row.profile_photo_path,
    row.profile_photo_storage_path,
    row.photo_path,
    row.avatar_path,
    row.email,
    row.phone,
  ].some(hasValue);
}

function hasMeaningfulPatientProfile(row) {
  if (!row?.user_id) return false;
  return [
    row.onboarding_completed,
    row.full_name,
    row.first_name,
    row.last_name,
    row.date_of_birth,
    row.street_address,
    row.city,
    row.country,
    row.languages,
    row.medical_conditions,
    row.medical_history,
    row.current_medications,
    row.allergies,
    row.blood_type,
    row.national_id_number,
    row.national_id,
    row.profile_photo_path,
    row.profile_photo_storage_path,
    row.photo_path,
    row.avatar_path,
    row.email,
    row.phone,
  ].some(hasValue);
}

function hasLikelyCompletedPatientOnboarding(row) {
  if (!row?.user_id) return false;
  const hasIdentity = hasValue(row?.national_id_number) || hasValue(row?.national_id);
  const hasCoreDemographics = hasValue(row?.date_of_birth);
  const hasHealthSummary =
    hasValue(row?.blood_type) ||
    hasValue(row?.allergies) ||
    hasValue(row?.medical_conditions) ||
    hasValue(row?.medical_history) ||
    hasValue(row?.current_medications);
  return hasIdentity && hasCoreDemographics && hasHealthSummary;
}

function hasOwnedIncompletePatientProgress(userId) {
  const uid = String(userId || "").trim();
  if (!uid) return false;

  try {
    const stored = loadProgress(PATIENT_PROGRESS_KEY) ?? null;
    if (String(stored?.ownerUserId || "").trim() !== uid) return false;
    return true;
  } catch {
    return false;
  }
}

function getExplicitSelectedRole(coreProfileRow) {
  if (coreProfileRow?.role_selected !== true) return "";
  return normalizeKnownRole(coreProfileRow?.role);
}

async function fetchUserRoleSet(userId) {
  const uid = String(userId || "").trim();
  if (!uid) return new Set();
  if (shouldSkipUserRoleLookup()) return new Set();

  try {
    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", uid);

    if (error) throw error;

    const roles = (Array.isArray(data) ? data : [])
      .map((row) => normalizeKnownRole(row?.role))
      .filter(Boolean);
    return new Set(roles);
  } catch (err) {
    if (err?.code === "42501" || err?.status === 403) {
      return new Set();
    }
    // eslint-disable-next-line no-console
    console.warn("[AuthProvider] user_roles lookup skipped:", err?.message || err);
    return new Set();
  }
}

async function fetchCoreProfileRow(userId) {
  const uid = String(userId || "").trim();
  if (!uid) return null;

  try {
    const { data, error } = await supabase
      .from("profiles")
      .select(
        "user_id,avatar_url,onboarding_completed,role,role_selected,preferred_app_language,doctor_settings_welcome_pending,doctor_settings_welcome_dismissed_at,patient_settings_welcome_pending,patient_settings_welcome_dismissed_at"
      )
      .eq("user_id", uid)
      .maybeSingle();
    if (error) throw error;
    return data || null;
  } catch (err) {
    // eslint-disable-next-line no-console
    throw err;
  }
}

async function fetchInviteRole(email) {
  const cleanEmail = String(email || "").trim().toLowerCase();
  if (!cleanEmail) return "";

  try {
    const { data, error } = await supabase
      .from("staging_access_invites")
      .select("role_hint,allowed,updated_at")
      .eq("email", cleanEmail)
      .eq("allowed", true)
      .order("updated_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return normalizeKnownRole(data?.role_hint === "clinician" ? "doctor" : data?.role_hint);
  } catch {
    return "";
  }
}

async function fetchPharmacyOwnerProfile(userId) {
  const uid = String(userId || "").trim();
  if (!uid) return null;

  try {
    const { data, error } = await supabase
      .from("pharmacy_connect_profiles")
      .select("user_id,pharmacy_name,trading_name,updated_at")
      .eq("user_id", uid)
      .maybeSingle();
    if (error) throw error;
    return data || null;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[AuthProvider] pharmacy profile lookup skipped:", err?.message || err);
    return null;
  }
}

async function fetchPracticeTeamMembership(userId) {
  const uid = String(userId || "").trim();
  if (!uid) return null;

  const cachedInvite = readCachedPracticeTeamInvite(uid);
  if (cachedInvite?.id) return cachedInvite;

  if (practiceTeamMembershipRequest?.userId === uid && practiceTeamMembershipRequest?.promise) {
    return practiceTeamMembershipRequest.promise;
  }

  const request = (async () => {
  try {
    let token = "";
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      token = String(sessionData?.session?.access_token || "").trim();
    } catch {
      token = "";
    }

    if (!token) {
      try {
        const { data: refreshed } = await supabase.auth.refreshSession();
        token = String(refreshed?.session?.access_token || "").trim();
      } catch {
        token = "";
      }
    }

    const { data, error } = await supabase.functions.invoke(
      "practice-team-invite-lifecycle",
      {
        body: {
          action: "welcome",
          member_id: "",
        },
        ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
      },
    );
    if (error) throw error;
    const invite = data?.invite || null;
    writeCachedPracticeTeamInvite(uid, invite);
    return invite;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[AuthProvider] Practice Team membership lookup skipped:", err?.message || err);
    return null;
  }
  })();

  practiceTeamMembershipRequest = { userId: uid, promise: request };
  try {
    return await request;
  } finally {
    if (practiceTeamMembershipRequest?.promise === request) {
      practiceTeamMembershipRequest = null;
    }
  }
}

// Helper: load doctor / patient profile from Supabase (dual-role aware)
async function fetchProfileForUser(user) {
  const userId = user?.id;
  if (!userId) return null;

  const emailFallback = user?.email || "";
  const metaName = resolveDisplayName(user, null);
  const metaAvatar = resolveAvatarFromUser(user);
  const requestedMetaRole =
    normalizeKnownRole(user?.user_metadata?.primary_role) ||
    normalizeKnownRole(user?.user_metadata?.role) ||
    normalizeKnownRole(user?.app_metadata?.role) ||
    normalizeKnownRole(user?.role);
  // Metadata describes onboarding intent; privileged entitlement comes from user_roles.
  const metaRole = ["patient", "doctor", "pharmacy"].includes(requestedMetaRole) ? requestedMetaRole : "";

  const readLastRole = () => readScopedLastRole(userId);

  const buildFallbackProfile = ({
    role = "",
    hasDoctor = false,
    hasPatient = false,
    onboardingCompleted = false,
    roleSelected = false,
    preferredAppLanguage = "",
    doctorSettingsWelcomePending = false,
    doctorSettingsWelcomeDismissedAt = "",
    patientSettingsWelcomePending = false,
    patientSettingsWelcomeDismissedAt = "",
    practiceTeamInvite = null,
    avatarUrl = "",
  } = {}) => {
    const fallbackName =
      metaName || (emailFallback ? emailFallback.split("@")[0] : "User");
    return {
      role,
      role_selected: roleSelected === true,
      hasDoctor,
      hasPatient,
      full_name: fallbackName,
      display_name: fallbackName,
      avatar_url: avatarUrl || metaAvatar || "",
      profile_image_url: avatarUrl || metaAvatar || "",
      email: emailFallback || "",
      onboarding_completed: !!onboardingCompleted,
      preferred_app_language: preferredAppLanguage || "",
      doctor_settings_welcome_pending: doctorSettingsWelcomePending === true,
      doctor_settings_welcome_dismissed_at:
        doctorSettingsWelcomeDismissedAt || "",
      patient_settings_welcome_pending: patientSettingsWelcomePending === true,
      patient_settings_welcome_dismissed_at:
        patientSettingsWelcomeDismissedAt || "",
      practice_team_invite: practiceTeamInvite || null,
    };
  };

  async function loadDoctorProfile({
    hasPatient = false,
    coreProfileRow = null,
    profileRow = null,
  } = {}) {
    let doctor = profileRow;
    if (!doctor) {
      const { data, error: doctorError } = await supabase
        .from("doctor_profiles")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();
      if (doctorError) throw doctorError;
      doctor = data;
    }

    const fullName =
      doctor?.full_name ||
      [doctor?.first_name, doctor?.last_name].filter(Boolean).join(" ") ||
      doctor?.display_name ||
      metaName;

    const signedProfileUrl = readAvatarSessionCache({ role: "doctor", userId });
    const signedCoverUrl = "";
    // Profile authority is ready independently of storage/network image work.

    return {
      ...(doctor || {}),
      preferred_app_language:
        coreProfileRow?.preferred_app_language ||
        doctor?.preferred_app_language ||
        "",
      doctor_settings_welcome_pending:
        coreProfileRow?.doctor_settings_welcome_pending === true,
      doctor_settings_welcome_dismissed_at:
        coreProfileRow?.doctor_settings_welcome_dismissed_at || "",
      patient_settings_welcome_pending:
        coreProfileRow?.patient_settings_welcome_pending === true,
      patient_settings_welcome_dismissed_at:
        coreProfileRow?.patient_settings_welcome_dismissed_at || "",
      role: "doctor",
      role_selected: coreProfileRow?.role_selected === true,
      hasDoctor: true,
      hasPatient,
      full_name: fullName,
      display_name: fullName || metaName,
      profile_photo_path:
        doctor?.profile_photo_path ||
        doctor?.profile_photo_storage_path ||
        doctor?.photo_path ||
        doctor?.avatar_path ||
        null,
      avatar_url:
        signedProfileUrl ||
        doctor?.avatar_url ||
        doctor?.profile_image_url ||
        "",
      profile_image_url:
        signedProfileUrl ||
        doctor?.profile_image_url ||
        doctor?.avatar_url ||
        "",
      cover_image_url: signedCoverUrl || doctor?.cover_image_url || "",
      email: doctor?.email || emailFallback || "",
      onboarding_completed:
        !!coreProfileRow?.onboarding_completed || !!doctor?.onboarding_completed,
    };
  }

  async function loadPatientProfile({
    hasDoctor = false,
    coreProfileRow = null,
    profileRow = null,
  } = {}) {
    let patient = profileRow;
    if (!patient) {
      const { data, error: patientError } = await supabase
        .from("patient_profiles")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();
      if (patientError) throw patientError;
      patient = data;
    }

    const fullName =
      patient?.full_name ||
      [patient?.first_name, patient?.last_name].filter(Boolean).join(" ") ||
      patient?.display_name ||
      metaName;

    const signedProfileUrl = readAvatarSessionCache({ role: "patient", userId });

    const preferredAvatar =
      signedProfileUrl ||
      patient?.profile_image_url ||
      patient?.avatar_url ||
      "";
    if (preferredAvatar) {
      writeAvatarSessionCache({ role: "patient", userId, src: preferredAvatar });
    }

    const hasIncompleteOwnedProgress =
      !coreProfileRow?.onboarding_completed && hasOwnedIncompletePatientProgress(userId);
    const inferredPatientDone =
      !!coreProfileRow?.onboarding_completed ||
      (!hasIncompleteOwnedProgress && hasLikelyCompletedPatientOnboarding(patient));

	    return {
	      ...(patient || {}),
      preferred_app_language:
        coreProfileRow?.preferred_app_language ||
        patient?.preferred_app_language ||
        "",
      doctor_settings_welcome_pending:
        coreProfileRow?.doctor_settings_welcome_pending === true,
      doctor_settings_welcome_dismissed_at:
        coreProfileRow?.doctor_settings_welcome_dismissed_at || "",
      patient_settings_welcome_pending:
        coreProfileRow?.patient_settings_welcome_pending === true,
      patient_settings_welcome_dismissed_at:
        coreProfileRow?.patient_settings_welcome_dismissed_at || "",
      role: "patient",
      role_selected: coreProfileRow?.role_selected === true,
      hasDoctor,
      hasPatient: true,
	      full_name: fullName,
	      display_name: fullName || metaName,
	      salutation:
	        patient?.salutation ||
	        user?.user_metadata?.salutation ||
	        "",
      profile_photo_path:
        patient?.profile_photo_path ||
        patient?.profile_photo_storage_path ||
        patient?.photo_path ||
        patient?.avatar_path ||
        null,
      avatar_url: preferredAvatar,
      profile_image_url: preferredAvatar,
      email: patient?.email || emailFallback || "",
      onboarding_completed: inferredPatientDone,
    };
  }

  try {
    // Institutional roles are already verified through user_roles. Do not hold
    // an authorised Institution workspace behind unrelated Doctor and Patient
    // profile probes: a MoH administrator has neither of those profiles by
    // design. All other role resolution continues through the established
    // dual-profile path below.
    const [userRoleSet, coreProfileRow] = await Promise.all([
      fetchUserRoleSet(userId),
      fetchCoreProfileRow(userId),
    ]);
    const institutionalRole =
      (userRoleSet.has("super_admin") && "super_admin") ||
      (userRoleSet.has("moh_admin") && "moh_admin") ||
      (userRoleSet.has("institution_admin") && "institution_admin") ||
      (userRoleSet.has("facility_admin") && "facility_admin") ||
      "";
    if (institutionalRole) {
      writeScopedLastRole(institutionalRole, userId);
      return buildFallbackProfile({
        role: institutionalRole,
        roleSelected: true,
        onboardingCompleted:
          institutionalRole === "moh_admin"
            ? !!coreProfileRow?.onboarding_completed
            : true,
        avatarUrl: coreProfileRow?.avatar_url || "",
        preferredAppLanguage: coreProfileRow?.preferred_app_language || "",
        doctorSettingsWelcomePending:
          coreProfileRow?.doctor_settings_welcome_pending === true,
        doctorSettingsWelcomeDismissedAt:
          coreProfileRow?.doctor_settings_welcome_dismissed_at || "",
        patientSettingsWelcomePending:
          coreProfileRow?.patient_settings_welcome_pending === true,
        patientSettingsWelcomeDismissedAt:
          coreProfileRow?.patient_settings_welcome_dismissed_at || "",
      });
    }

    const [docProbe, patProbe] = await Promise.all([
      supabase
        .from("doctor_profiles")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle(),
      supabase
        .from("patient_profiles")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle(),
    ]);

    if (docProbe?.error) throw docProbe.error;
    if (patProbe?.error) throw patProbe.error;

    const doctorProbeRow = docProbe?.data || null;
    const patientProbeRow = patProbe?.data || null;

    const hasDoctorRaw = !!doctorProbeRow?.user_id;
    const hasPatientRaw = !!patientProbeRow?.user_id;
    const hasDirectProfileOrRole =
      hasDoctorRaw ||
      hasPatientRaw ||
      userRoleSet.size > 0 ||
      Boolean(normalizeKnownRole(coreProfileRow?.role)) ||
      Boolean(metaRole);

    // Invite and membership resolution is essential for roleless / invitation
    // flows, but it must not hold back a known Patient or Clinician profile.
    // In particular, the Practice Team lookup can be much slower than the
    // profile reads and was delaying every avatar-bearing authenticated view.
    let inviteRole = null;
    let pharmacyOwnerProfile = null;
    let practiceTeamMembership = null;
    if (!hasDirectProfileOrRole) {
      [inviteRole, pharmacyOwnerProfile, practiceTeamMembership] = await Promise.all([
        fetchInviteRole(emailFallback),
        fetchPharmacyOwnerProfile(userId),
        fetchPracticeTeamMembership(userId),
      ]);
    }
    const hasAdminRole = userRoleSet.has("admin");
    const adminTeamRole =
      (userRoleSet.has("super_admin") && "super_admin") ||
      (userRoleSet.has("admin") && "admin") ||
      (userRoleSet.has("support") && "support") ||
      (userRoleSet.has("kyc_reviewer") && "kyc_reviewer") ||
      "";
    const explicitSelectedRole = getExplicitSelectedRole(coreProfileRow);
    const hasPharmacyRole =
      userRoleSet.has("pharmacy") ||
      metaRole === "pharmacy" ||
      normalizeKnownRole(coreProfileRow?.role) === "pharmacy" ||
      !!pharmacyOwnerProfile?.user_id;
    const pharmacyOnboardingCompleted =
      !!coreProfileRow?.onboarding_completed || !!pharmacyOwnerProfile?.user_id;
    const doctorUsable = hasMeaningfulDoctorProfile(doctorProbeRow);
    const patientUsable = hasMeaningfulPatientProfile(patientProbeRow);
    const hasDoctorRole = userRoleSet.has("doctor");
    const hasPatientRole = userRoleSet.has("patient");
    const hasExplicitRoleSet = userRoleSet.size > 0;
    const roleSelectionPending = coreProfileRow?.role_selected === false;
    const canonicalRoleResolution = resolveCanonicalRole({
      inviteRole,
      metadataRole: metaRole,
      profileRole: coreProfileRow?.role || "",
      roleSelected: coreProfileRow?.role_selected === true,
      doctorProfile: doctorProbeRow,
      patientProfile: patientProbeRow,
      practiceTeamMembership: !!practiceTeamMembership?.id,
      userRoles: Array.from(userRoleSet),
      fallbackRole: readLastRole(),
    });
    const canonicalRole = normalizeKnownRole(canonicalRoleResolution.role);

    if (roleSelectionPending && !canonicalRole) {
      clearScopedLastRole(userId);
      return buildFallbackProfile({
        role: "",
        roleSelected: false,
        hasDoctor: false,
        hasPatient: false,
        onboardingCompleted: false,
        preferredAppLanguage: coreProfileRow?.preferred_app_language || "",
        doctorSettingsWelcomePending:
          coreProfileRow?.doctor_settings_welcome_pending === true,
        doctorSettingsWelcomeDismissedAt:
          coreProfileRow?.doctor_settings_welcome_dismissed_at || "",
        patientSettingsWelcomePending:
          coreProfileRow?.patient_settings_welcome_pending === true,
        patientSettingsWelcomeDismissedAt:
          coreProfileRow?.patient_settings_welcome_dismissed_at || "",
        practiceTeamInvite: practiceTeamMembership || null,
        avatarUrl: coreProfileRow?.avatar_url || "",
      });
    }

    if (canonicalRole === "doctor") {
      writeScopedLastRole("doctor", userId);
      try {
        if (hasDoctorRaw) {
          return await loadDoctorProfile({
            hasPatient: hasPatientRaw,
            coreProfileRow,
            profileRow: doctorProbeRow,
          });
        }
      } catch (err) {
        console.error("[AuthProvider] loadDoctorProfile fallback:", err);
      }
      return buildFallbackProfile({
        role: "doctor",
        roleSelected: coreProfileRow?.role_selected === true,
        hasDoctor: true,
        hasPatient: hasPatientRaw,
        onboardingCompleted: !!coreProfileRow?.onboarding_completed,
        preferredAppLanguage: coreProfileRow?.preferred_app_language || "",
        doctorSettingsWelcomePending:
          coreProfileRow?.doctor_settings_welcome_pending === true,
        doctorSettingsWelcomeDismissedAt:
          coreProfileRow?.doctor_settings_welcome_dismissed_at || "",
        patientSettingsWelcomePending:
          coreProfileRow?.patient_settings_welcome_pending === true,
        patientSettingsWelcomeDismissedAt:
          coreProfileRow?.patient_settings_welcome_dismissed_at || "",
      });
    }

    if (canonicalRole === "pharmacy") {
      writeScopedLastRole("pharmacy", userId);
      return buildFallbackProfile({
        role: "pharmacy",
        roleSelected: coreProfileRow?.role_selected === true,
        hasDoctor: hasDoctorRaw,
        hasPatient: hasPatientRaw,
        onboardingCompleted: pharmacyOnboardingCompleted,
        preferredAppLanguage: coreProfileRow?.preferred_app_language || "",
        doctorSettingsWelcomePending:
          coreProfileRow?.doctor_settings_welcome_pending === true,
        doctorSettingsWelcomeDismissedAt:
          coreProfileRow?.doctor_settings_welcome_dismissed_at || "",
        patientSettingsWelcomePending:
          coreProfileRow?.patient_settings_welcome_pending === true,
        patientSettingsWelcomeDismissedAt:
          coreProfileRow?.patient_settings_welcome_dismissed_at || "",
      });
    }

    if (canonicalRole === "practice_team") {
      writeScopedLastRole("practice_team", userId);
      return buildFallbackProfile({
        role: "practice_team",
        roleSelected: coreProfileRow?.role_selected === true,
        hasDoctor: hasDoctorRaw,
        hasPatient: hasPatientRaw,
        onboardingCompleted: true,
        preferredAppLanguage: coreProfileRow?.preferred_app_language || "",
        doctorSettingsWelcomePending:
          coreProfileRow?.doctor_settings_welcome_pending === true,
        doctorSettingsWelcomeDismissedAt:
          coreProfileRow?.doctor_settings_welcome_dismissed_at || "",
        patientSettingsWelcomePending:
          coreProfileRow?.patient_settings_welcome_pending === true,
        patientSettingsWelcomeDismissedAt:
          coreProfileRow?.patient_settings_welcome_dismissed_at || "",
        // Account and workspace guards need the same verified membership
        // resolution that selected this role. Omitting it made a valid member
        // look unauthorised after navigation and redirected /account to
        // /practice-team/welcome.
        practiceTeamInvite: practiceTeamMembership || null,
      });
    }

    if (canonicalRole === "patient" && canonicalRoleResolution.source !== "fallback_hint") {
      writeScopedLastRole("patient", userId);
      try {
        if (hasPatientRaw) {
          return await loadPatientProfile({
            hasDoctor: hasDoctorRaw,
            coreProfileRow,
            profileRow: patientProbeRow,
          });
        }
      } catch (err) {
        console.error("[AuthProvider] loadPatientProfile fallback:", err);
      }
      return buildFallbackProfile({
        role: "patient",
        roleSelected: coreProfileRow?.role_selected === true,
        hasDoctor: hasDoctorRaw,
        hasPatient: true,
        onboardingCompleted: !!coreProfileRow?.onboarding_completed,
        preferredAppLanguage: coreProfileRow?.preferred_app_language || "",
        doctorSettingsWelcomePending:
          coreProfileRow?.doctor_settings_welcome_pending === true,
        doctorSettingsWelcomeDismissedAt:
          coreProfileRow?.doctor_settings_welcome_dismissed_at || "",
        patientSettingsWelcomePending:
          coreProfileRow?.patient_settings_welcome_pending === true,
        patientSettingsWelcomeDismissedAt:
          coreProfileRow?.patient_settings_welcome_dismissed_at || "",
      });
    }

    if (institutionalRole || (
      canonicalRole === "super_admin" ||
      canonicalRole === "moh_admin" ||
      canonicalRole === "institution_admin" ||
      canonicalRole === "facility_admin"
    )) {
      const resolvedInstitutionRole = institutionalRole || canonicalRole;
      writeScopedLastRole(resolvedInstitutionRole, userId);
      return buildFallbackProfile({
        role: resolvedInstitutionRole,
        roleSelected: true,
        hasDoctor: false,
        hasPatient: false,
        // A globally eligible MoH administrator may legitimately have no
        // institution workspace yet. Preserve its durable completion state so
        // /onboarding/moh can provision that first governed workspace instead
        // of being intercepted by the generic onboarding-complete redirect.
        onboardingCompleted:
          resolvedInstitutionRole === "moh_admin"
            ? !!coreProfileRow?.onboarding_completed
            : true,
        avatarUrl:
          coreProfileRow?.avatar_url || "",
        preferredAppLanguage: coreProfileRow?.preferred_app_language || "",
        doctorSettingsWelcomePending:
          coreProfileRow?.doctor_settings_welcome_pending === true,
        doctorSettingsWelcomeDismissedAt:
          coreProfileRow?.doctor_settings_welcome_dismissed_at || "",
        patientSettingsWelcomePending:
          coreProfileRow?.patient_settings_welcome_pending === true,
        patientSettingsWelcomeDismissedAt:
          coreProfileRow?.patient_settings_welcome_dismissed_at || "",
      });
    }

    let hasDoctor = hasDoctorRaw;
    let hasPatient = hasPatientRaw;
    if (explicitSelectedRole === "doctor") {
      hasDoctor = hasDoctorRaw;
      hasPatient = false;
    } else if (explicitSelectedRole === "patient") {
      hasDoctor = false;
      hasPatient = hasPatientRaw;
    } else if (hasDoctorRole !== hasPatientRole) {
      hasDoctor = hasDoctorRole;
      hasPatient = hasPatientRole;
    } else if (hasDoctorRaw && hasPatientRaw) {
      if (doctorUsable && !patientUsable) {
        hasPatient = false;
      } else if (!doctorUsable && patientUsable) {
        hasDoctor = false;
      }
    } else if (!hasExplicitRoleSet) {
      const doctorDone =
        !!coreProfileRow?.onboarding_completed || !!doctorProbeRow?.onboarding_completed;
          const patientDone =
            !!coreProfileRow?.onboarding_completed ||
            (!hasOwnedIncompletePatientProgress(userId) &&
              hasLikelyCompletedPatientOnboarding(patientProbeRow));
      hasDoctor = hasDoctorRaw && doctorDone;
      hasPatient = hasPatientRaw && patientDone;
    }

    const adminRole = adminTeamRole || hasAdminRole;

    if (adminRole) {
      const resolvedAdminRole = adminTeamRole || "admin";
      writeScopedLastRole(resolvedAdminRole, userId);
      return buildFallbackProfile({
        role: resolvedAdminRole,
        onboardingCompleted: true,
        preferredAppLanguage: coreProfileRow?.preferred_app_language || "",
        doctorSettingsWelcomePending:
          coreProfileRow?.doctor_settings_welcome_pending === true,
        doctorSettingsWelcomeDismissedAt:
          coreProfileRow?.doctor_settings_welcome_dismissed_at || "",
        patientSettingsWelcomePending:
          coreProfileRow?.patient_settings_welcome_pending === true,
        patientSettingsWelcomeDismissedAt:
          coreProfileRow?.patient_settings_welcome_dismissed_at || "",
      });
    }

    if (!hasDoctor && !hasPatient && hasPharmacyRole) {
      writeScopedLastRole("pharmacy", userId);
      return buildFallbackProfile({
        role: "pharmacy",
        roleSelected: coreProfileRow?.role_selected === true,
        onboardingCompleted: pharmacyOnboardingCompleted,
        preferredAppLanguage: coreProfileRow?.preferred_app_language || "",
        doctorSettingsWelcomePending:
          coreProfileRow?.doctor_settings_welcome_pending === true,
        doctorSettingsWelcomeDismissedAt:
          coreProfileRow?.doctor_settings_welcome_dismissed_at || "",
        patientSettingsWelcomePending:
          coreProfileRow?.patient_settings_welcome_pending === true,
        patientSettingsWelcomeDismissedAt:
          coreProfileRow?.patient_settings_welcome_dismissed_at || "",
      });
    }

    if (hasDoctor && !hasPatient) {
      writeScopedLastRole("doctor", userId);
      try {
        return await loadDoctorProfile({
          hasPatient: false,
          coreProfileRow,
          profileRow: doctorProbeRow,
        });
      } catch (err) {
        console.error("[AuthProvider] loadDoctorProfile fallback:", err);
        return buildFallbackProfile({
          role: "doctor",
          hasDoctor: true,
          onboardingCompleted: !!coreProfileRow?.onboarding_completed,
          preferredAppLanguage: coreProfileRow?.preferred_app_language || "",
          doctorSettingsWelcomePending:
            coreProfileRow?.doctor_settings_welcome_pending === true,
          doctorSettingsWelcomeDismissedAt:
            coreProfileRow?.doctor_settings_welcome_dismissed_at || "",
          patientSettingsWelcomePending:
            coreProfileRow?.patient_settings_welcome_pending === true,
          patientSettingsWelcomeDismissedAt:
            coreProfileRow?.patient_settings_welcome_dismissed_at || "",
        });
      }
    }

    if (!hasDoctor && hasPatient) {
      writeScopedLastRole("patient", userId);
      try {
        return await loadPatientProfile({
          hasDoctor: false,
          coreProfileRow,
          profileRow: patientProbeRow,
        });
      } catch (err) {
        console.error("[AuthProvider] loadPatientProfile fallback:", err);
        return buildFallbackProfile({
          role: "patient",
          hasPatient: true,
          onboardingCompleted: !!coreProfileRow?.onboarding_completed,
          preferredAppLanguage: coreProfileRow?.preferred_app_language || "",
          doctorSettingsWelcomePending:
            coreProfileRow?.doctor_settings_welcome_pending === true,
          doctorSettingsWelcomeDismissedAt:
            coreProfileRow?.doctor_settings_welcome_dismissed_at || "",
          patientSettingsWelcomePending:
            coreProfileRow?.patient_settings_welcome_pending === true,
          patientSettingsWelcomeDismissedAt:
            coreProfileRow?.patient_settings_welcome_dismissed_at || "",
        });
      }
    }

    if (hasDoctor && hasPatient) {
      const chosenRole = readLastRole();
      if (doctorUsable && !patientUsable) {
        return await loadDoctorProfile({
          hasPatient: false,
          coreProfileRow,
          profileRow: doctorProbeRow,
        });
      }
      if (!doctorUsable && patientUsable) {
        return await loadPatientProfile({
          hasDoctor: false,
          coreProfileRow,
          profileRow: patientProbeRow,
        });
      }
      if (chosenRole !== "doctor" && chosenRole !== "patient") {
        return buildFallbackProfile({
          role: "dual",
          hasDoctor: true,
          hasPatient: true,
          preferredAppLanguage: coreProfileRow?.preferred_app_language || "",
          doctorSettingsWelcomePending:
            coreProfileRow?.doctor_settings_welcome_pending === true,
          doctorSettingsWelcomeDismissedAt:
            coreProfileRow?.doctor_settings_welcome_dismissed_at || "",
          patientSettingsWelcomePending:
            coreProfileRow?.patient_settings_welcome_pending === true,
          patientSettingsWelcomeDismissedAt:
            coreProfileRow?.patient_settings_welcome_dismissed_at || "",
        });
      }

      try {
        if (chosenRole === "doctor") {
          return await loadDoctorProfile({
            hasPatient: true,
            coreProfileRow,
            profileRow: doctorProbeRow,
          });
        }
        return await loadPatientProfile({
          hasDoctor: true,
          coreProfileRow,
          profileRow: patientProbeRow,
        });
      } catch (err) {
        console.error("[AuthProvider] loadDualRoleProfile fallback:", err);
        return buildFallbackProfile({
          role: chosenRole,
          hasDoctor: true,
          hasPatient: true,
          onboardingCompleted: !!coreProfileRow?.onboarding_completed,
          preferredAppLanguage: coreProfileRow?.preferred_app_language || "",
          doctorSettingsWelcomePending:
            coreProfileRow?.doctor_settings_welcome_pending === true,
          doctorSettingsWelcomeDismissedAt:
            coreProfileRow?.doctor_settings_welcome_dismissed_at || "",
          patientSettingsWelcomePending:
            coreProfileRow?.patient_settings_welcome_pending === true,
          patientSettingsWelcomeDismissedAt:
            coreProfileRow?.patient_settings_welcome_dismissed_at || "",
        });
      }
    }

    // No profile rows yet (new signup / onboarding pending)
    const pendingRole =
      coreProfileRow && coreProfileRow.role_selected !== true ? "" : metaRole || "";
    return buildFallbackProfile({
      role: pendingRole,
      roleSelected: coreProfileRow?.role_selected === true,
      onboardingCompleted: !!coreProfileRow?.onboarding_completed,
      preferredAppLanguage: coreProfileRow?.preferred_app_language || "",
      doctorSettingsWelcomePending:
        coreProfileRow?.doctor_settings_welcome_pending === true,
      doctorSettingsWelcomeDismissedAt:
        coreProfileRow?.doctor_settings_welcome_dismissed_at || "",
      patientSettingsWelcomePending:
        coreProfileRow?.patient_settings_welcome_pending === true,
      patientSettingsWelcomeDismissedAt:
        coreProfileRow?.patient_settings_welcome_dismissed_at || "",
    });
  } catch (err) {
    console.error("[AuthProvider] fetchProfileForUser error:", err);
    // A failed read is not evidence of a new/incomplete account.
    throw err;
  }
}

// Supabase emits INITIAL_SESSION while the provider also resolves the current
// session on mount. Both paths need the same authoritative profile truth, but
// they must share one in-flight lookup rather than independently rehydrating
// the same user and delaying the first protected screen.
async function hydrateProfileTruth(user) {
  const userId = String(user?.id || "").trim();
  if (!userId) return null;

  if (profileHydrationRequest?.userId === userId && profileHydrationRequest?.promise) {
    return profileHydrationRequest.promise;
  }

  const request = (async () => {
    await ensureProfileFirstWrite(user);
    return fetchProfileForUser(user);
  })();
  profileHydrationRequest = { userId, promise: request };

  try {
    return await request;
  } finally {
    if (profileHydrationRequest?.promise === request) {
      profileHydrationRequest = null;
    }
  }
}

const AuthContext = createContext({
  user: null,
  profile: null,
  loading: false,
  profileResolved: true,
  loadingPhase: "idle",
  loadingProgress: 0,
  refreshProfile: async () => {}, // ✅ Add this default to the context definition
  BYPASS: false,
  DEFAULT_ROLE: "patient",
});

export function AuthProvider({ children }) {
  const BYPASS = canBypassAuth();
  const DEFAULT_ROLE = String(import.meta.env.VITE_DEV_ROLE || "patient").toLowerCase();
  const DEV_BYPASS_USER_ID = String(import.meta.env.VITE_DEV_BYPASS_USER_ID || "dev-user").trim() || "dev-user";
  const DEV_BYPASS_EMAIL =
    String(import.meta.env.VITE_DEV_BYPASS_EMAIL || `${DEFAULT_ROLE}.dev@afrimed.com`).trim() ||
    `${DEFAULT_ROLE}.dev@afrimed.com`;
  const DEV_BYPASS_FULL_NAME =
    String(
      import.meta.env.VITE_DEV_BYPASS_FULL_NAME ||
        (DEFAULT_ROLE === "doctor" ? "Dr. Dev" : "Dev User"),
    ).trim() || (DEFAULT_ROLE === "doctor" ? "Dr. Dev" : "Dev User");

  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileResolved, setProfileResolved] = useState(false);
  const [profileError, setProfileError] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState("boot");
  const [loadingProgress, setLoadingProgress] = useState(8);
  const userRef = useRef(null);
  const profileRef = useRef(null);
  const authBootStartedAtRef = useRef(performance.now());
  const authReadyTracedRef = useRef(false);
  // Auth changes can arrive from Supabase and the browser storage event in
  // either order. A monotonically increasing epoch prevents an older async
  // profile lookup from restoring a former workspace after a newer login.
  const authChangeEpochRef = useRef(0);

  useEffect(() => {
    userRef.current = user;
    profileRef.current = profile;
  }, [user, profile]);

  const buildBypassUser = useCallback(
    () => ({
      id: DEV_BYPASS_USER_ID,
      email: DEV_BYPASS_EMAIL,
      app_metadata: {
        bypass_dev_identity: true,
      },
      user_metadata: {
        full_name: DEV_BYPASS_FULL_NAME,
        bypass_dev_identity: "true",
      },
    }),
    [DEFAULT_ROLE, DEV_BYPASS_EMAIL, DEV_BYPASS_FULL_NAME, DEV_BYPASS_USER_ID]
  );

  const buildBypassProfile = useCallback(
    () => {
      const fakeUser = buildBypassUser();
      return {
        role: DEFAULT_ROLE,
        full_name: fakeUser.user_metadata.full_name,
        display_name: fakeUser.user_metadata.full_name,
        email: fakeUser.email,
        avatar_url: "",
        profile_image_url: "",
      };
    },
    [DEFAULT_ROLE, buildBypassUser]
  );

  // ✅ THE REFRESH HANDSHAKE: Logic to force-update the profile state
  const refreshProfile = useCallback(async () => {
    if (!user) return;
    const shouldBlockForInitialTruth = !profileRef.current;
    if (shouldBlockForInitialTruth) setProfileResolved(false);
    setProfileError(false);
    try {
      const updatedProfile = await hydrateProfileTruth(user);
      if (userRef.current?.id !== user.id) return;
      setProfile(updatedProfile);
      setProfileResolved(true);
      return updatedProfile;
    } catch {
      setProfileError(!profileRef.current);
      setProfileResolved(Boolean(profileRef.current));
    }
  }, [user]);

  useEffect(() => {
    let alive = true;
    let unsubscribeAuthState = null;
    const bootFailsafe = setTimeout(() => {
      if (!alive || authReadyTracedRef.current) return;
      setLoadingPhase("timeout");
      setLoadingProgress(100);
      setProfileResolved(Boolean(profileRef.current));
      setProfileError(!profileRef.current);
      setLoading(false);
      if (!authReadyTracedRef.current) {
        authReadyTracedRef.current = true;
        recordStagingPerformance("auth_ready", {
          durationMs: Math.round(performance.now() - authBootStartedAtRef.current),
          outcome: "timeout",
        });
      }
    }, 15000);

    async function hydrateAuthStateChange(event, sess) {
      if (!alive) return;
      const authEpoch = ++authChangeEpochRef.current;

      // Do not blank the whole app for routine auth refreshes.
      // We only hard-block on sign-out, where protected routes must collapse.
      const shouldHydrateHard = event === "SIGNED_OUT";
      if (shouldHydrateHard) {
        setLoading(true);
        setLoadingPhase("auth-change");
        setLoadingProgress(28);
      }
      const sessionUser = sess?.user || null;
      const useBypassIdentity = BYPASS && !sessionUser;
      const nextUser = useBypassIdentity ? buildBypassUser() : sessionUser;
      const previousUser = userRef.current;
      const previousProfile = profileRef.current;
      const isSameSignedInUser =
        !!previousUser?.id && !!nextUser?.id && String(previousUser.id) === String(nextUser.id);
      const hasUsableProfile = !!previousProfile;
      const shouldBlockForProfileTruth = !isSameSignedInUser || !hasUsableProfile;

      if (!isSameSignedInUser) clearCachedPracticeTeamInvite();
      if (shouldBlockForProfileTruth) setProfileResolved(false);
      setUser(nextUser);
      if (nextUser?.id && !useBypassIdentity) restoreUserThemePreference(nextUser);
      if (!isSameSignedInUser) setProfile(null);

      if (useBypassIdentity) {
        setProfile(buildBypassProfile());
        setProfileResolved(true);
      } else if (sessionUser) {
        if (shouldHydrateHard) {
          setLoadingPhase("profile");
          setLoadingProgress(68);
        }
        const loadedProfile = await hydrateProfileTruth(sessionUser);
        if (!alive || authEpoch !== authChangeEpochRef.current) return;
        setProfile(loadedProfile);
        setProfileError(false);
        setProfileResolved(true);
      } else {
        setProfile(null);
        setProfileError(false);
        setProfileResolved(true);
      }
      if (shouldHydrateHard) {
        clearTimeout(bootFailsafe);
        setLoadingPhase("ready");
        setLoadingProgress(100);
        setLoading(false);
      }
    }

    const { data: sub } = supabase.auth.onAuthStateChange((event, sess) => {
      hydrateAuthStateChange(event, sess).catch((eventErr) => {
        // eslint-disable-next-line no-console
        console.error("[AuthProvider] onAuthStateChange error:", eventErr);
        if (!alive) return;
        const shouldHydrateHard = event === "SIGNED_OUT";
        setProfileResolved(Boolean(profileRef.current));
        setProfileError(!profileRef.current);
        if (shouldHydrateHard) {
          setLoadingPhase("error");
          setLoadingProgress(100);
          setLoading(false);
        }
      });
    });
    unsubscribeAuthState = () => sub?.subscription?.unsubscribe?.();

    // Supabase broadcasts normal auth events, but a same-origin browser tab can
    // also replace the persisted session before a mounted delegated workspace
    // observes one. Re-resolve it here so no former member content survives a
    // cross-tab login.
    const handleSessionStorageChange = (event) => {
      if (!event.key || !/auth-token$/i.test(event.key)) return;
      resolveValidatedSession({ signOutOnFailure: false })
        .then((nextSession) => hydrateAuthStateChange("STORAGE_CHANGE", nextSession))
        .catch(() => hydrateAuthStateChange("SIGNED_OUT", null));
    };
    window.addEventListener("storage", handleSessionStorageChange);

    async function init() {
      try {
        setProfileResolved(false);
        setLoadingPhase("session");
        setLoadingProgress(20);

        const session = await resolveValidatedSession({ signOutOnFailure: false });

        if (!alive) return;

        recordStagingPerformance("auth_session_resolved", {
          durationMs: Math.round(performance.now() - authBootStartedAtRef.current),
          authenticated: Boolean(session?.user?.id),
        });

        const sessUser = session?.user || null;
        const useBypassIdentity = BYPASS && !sessUser;
        const nextUser = useBypassIdentity ? buildBypassUser() : sessUser;

        setUser(nextUser);
        if (nextUser?.id && !useBypassIdentity) restoreUserThemePreference(nextUser);

        if (useBypassIdentity) {
          setProfile(buildBypassProfile());
          setProfileResolved(true);
        } else if (sessUser) {
          setLoadingPhase("profile");
          setLoadingProgress(68);
          const loadedProfile = await hydrateProfileTruth(sessUser);
          if (!alive) return;
          setProfile(loadedProfile);
          setProfileError(false);
          setProfileResolved(true);
          recordStagingPerformance("auth_profile_ready", {
            durationMs: Math.round(performance.now() - authBootStartedAtRef.current),
            role: normalizeKnownRole(loadedProfile?.role) || "unknown",
          });
        } else {
          setLoadingPhase("ready");
          setLoadingProgress(100);
          setProfile(null);
          setProfileResolved(true);
        }

        clearTimeout(bootFailsafe);
        setLoadingPhase("ready");
        setLoadingProgress(100);
        setLoading(false);
        if (!authReadyTracedRef.current) {
          authReadyTracedRef.current = true;
          recordStagingPerformance("auth_ready", {
            durationMs: Math.round(performance.now() - authBootStartedAtRef.current),
            authenticated: Boolean(nextUser?.id),
          });
        }
      } catch (err) {
        console.error("[AuthProvider] init error:", err);
        if (!alive) return;
        setLoadingPhase("error");
        setLoadingProgress(100);
        setProfileResolved(Boolean(profileRef.current));
        setProfileError(!profileRef.current);
        setLoading(false);
        if (!authReadyTracedRef.current) {
          authReadyTracedRef.current = true;
          recordStagingPerformance("auth_ready", {
            durationMs: Math.round(performance.now() - authBootStartedAtRef.current),
            outcome: "error",
          });
        }
      }
    }

    init();

    return () => {
      alive = false;
      clearTimeout(bootFailsafe);
      unsubscribeAuthState?.();
      window.removeEventListener("storage", handleSessionStorageChange);
    };
  }, [BYPASS, buildBypassProfile, buildBypassUser]);

  useEffect(() => {
    if (isDevBypassIdentity(user) || !user?.id) return undefined;

    const handleStorageRefresh = (event) => {
      if (event?.key !== "afrimed:profile:refresh") return;
      void refreshProfile();
    };

    window.addEventListener("storage", handleStorageRefresh);

    return () => {
      window.removeEventListener("storage", handleStorageRefresh);
    };
  }, [user, user?.id, refreshProfile]);

  useEffect(() => {
    if (isDevBypassIdentity(user) || !user?.id) return undefined;

    const channel = supabase
      .channel(`profile-sync-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "profiles",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          void refreshProfile();
        }
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "doctor_profiles",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          void refreshProfile();
        }
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "patient_profiles",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          void refreshProfile();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, user?.id, refreshProfile]);

  // Refresh private image URLs after profile truth has been published. Consumers
  // share the signing cache; neither cover nor avatar delays auth/onboarding.
  useEffect(() => {
    const uid = user?.id;
    const role = profile?.role;
    if (!uid || !["doctor", "patient"].includes(role)) return undefined;
    let alive = true;
    const path = profile?.profile_photo_path;
    if (path) void signProfileAsset(supabase, `${role}-profile-images`, path).then(({ data }) => {
      if (!alive || userRef.current?.id !== uid || !data?.signedUrl) return;
      writeAvatarSessionCache({ role, userId: uid, src: data.signedUrl });
      setProfile(current => current?.profile_photo_path === path ? { ...current, avatar_url: data.signedUrl, profile_image_url: data.signedUrl } : current);
    });
    const cover = profile?.cover_photo_path;
    if (cover) void signProfileAsset(supabase, "cover-images", cover).then(({ data }) => {
      if (!alive || userRef.current?.id !== uid || !data?.signedUrl) return;
      setProfile(current => current?.cover_photo_path === cover ? { ...current, cover_image_url: data.signedUrl } : current);
    });
    return () => { alive = false; };
  }, [user?.id, profile?.role, profile?.profile_photo_path, profile?.cover_photo_path]);

  const value = useMemo(
    () => ({
      user,
      profile,
      loading,
      profileResolved,
      loadingPhase,
      profileError,
      loadingProgress,
      refreshProfile, // ✅ Added to the memoized value
      BYPASS: BYPASS,
      DEFAULT_ROLE
    }),
    [user, profile, loading, profileResolved, loadingPhase, profileError, loadingProgress, refreshProfile, BYPASS, DEFAULT_ROLE]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext) || {};
}
